/**
 * Random number utilities.
 *
 * Separates cryptographically secure randomness (for IDs, codes, tokens)
 * from non-secure randomness (for visuals, jitter, shuffling).
 */

// ---------------------------------------------------------------------------
// Cryptographically secure — use for IDs, invite codes, session tokens
// ---------------------------------------------------------------------------

/** Cryptographically secure random integer in [0, max). */
function secureRandomInt(max: number): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % max;
}

/** Generate a cryptographically secure alphanumeric code (excludes ambiguous chars). */
export function generateSecureCode(length: number): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(secureRandomInt(chars.length));
  }
  return result;
}

/** Generate a cryptographically secure unique ID string. */
export function generateSecureId(prefix = ''): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return prefix ? `${prefix}${hex}` : hex;
}

// ---------------------------------------------------------------------------
// Non-secure — safe for visuals, animations, jitter, shuffling, demo data.
// Intentionally uses Math.random() for performance; these are NOT
// security-sensitive and do not need cryptographic guarantees.
// ---------------------------------------------------------------------------

/* eslint-disable sonarjs/pseudo-random */

/** Random float in [0, 1). For visuals, animations, jitter. */
export function random(): number {
  return Math.random();
}

/** Random float in [min, max). */
export function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Random integer in [0, max). */
export function randomInt(max: number): number {
  return Math.floor(Math.random() * max);
}

/** Random element from an array. */
export function randomPick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Generate a non-secure unique-ish ID (for UI keys, toast IDs, etc.). */
export function randomId(prefix = ''): string {
  const part = Math.random().toString(36).substring(2, 10);
  const p = prefix ? `${prefix}-` : '';
  return `${p}${Date.now().toString(36)}-${part}`;
}

/** Fisher-Yates shuffle (in-place). */
export function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Random jitter: returns a value in [0, maxMs) for staggering retries. */
export function jitterMs(maxMs: number): number {
  return Math.random() * maxMs;
}

/* eslint-enable sonarjs/pseudo-random */
